import _, { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Loader, PercentChangeIndicator } from '../../../common/components';
import DashboardInfoLabel from '../utils/DashboardInfoLabel';
import DashboardTooltip from '../utils/DashboardTooltip';
import {
  currencyFormatter,
  currencyFormatterInShort,
  DASHBOARD_COLORS,
} from '../utils/dashboardConstants';
import ARAgingGraph from './AR-Aging';

function BilledVsPaidCard({ title, tooltip, data, loader }) {
  const graphId = useMemo(() => uniqueId('billed-paid-graph-id'), []);

  return (
    <div className="billed-vs-paid-wrapper">
      <DashboardInfoLabel
        id={uniqueId('billed-paid-main-title')}
        label={title}
        showIcon
        className="card-title-info-label"
        toolTipText={tooltip.cardTooltip}
      />
      {loader ? (
        <Loader />
      ) : (
        <div className="billed-vs-paid">
          <ARAgingGraph
            id={graphId}
            loader={false}
            data={{
              smallDataLabels: true,
              graphType: 'bar',
              graphMode: 'Combo',
              title: 'Billed vs Paid',
              graphColor: DASHBOARD_COLORS.BILLED_VS_PAID,
              graphTitle: {
                x: 'ENTITY',
              },
              impNote: '',
              series: [
                {
                  name: 'Billed',
                  type: 'bar',
                  data: data.values.billed,
                },
                {
                  name: 'Paid',
                  type: 'bar',
                  data: data.values.paid,
                },
                {
                  name: 'Past Due',
                  type: 'bar',
                  data: data.values.pastDue,
                },
              ],
              showDataLabels: true,
              legendRequired: false,
              xLabels: data.labels,
              redirection: data.redirection,
            }}
          />
          <div className="dashboard-overview-card">
            <Card className="dashboard-overview-card-layout">
              <CardHeader className="card-header-wrapper" style={{ backgroundColor: '#F1FFF2' }}>
                <div className="title-date-wrapper">
                  <DashboardInfoLabel
                    id={uniqueId('billed-paid-cash-flow')}
                    label="Total Cashflow"
                    showIcon
                    toolTipText={tooltip.totalCashflowTooltip}
                  />
                  <p className="card-date">
                    {`Last updated: ${
                      data.lastUpdatedAt
                        ? new Date(data.lastUpdatedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '-'
                    }`}
                  </p>
                  <div className="amount-percent-wrapper">
                    <h6 className="card-price">
                      <DashboardTooltip
                        id={uniqueId('bvp-total-cashflow')}
                        position="bottom"
                        value={_.sum(data.values.paid)}
                        label={currencyFormatterInShort(_.sum(data.values.paid))}
                        tooltipText={currencyFormatter(_.sum(data.values.paid))}
                      />
                    </h6>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="dashboard-overview-card-body">
                <div className="billed-paid-item">
                  <p
                    className="color-bar"
                    style={{
                      backgroundColor: DASHBOARD_COLORS.BILLED_VS_PAID[0],
                    }}
                  />
                  <div className="label-value">
                    <p className="label">Billed</p>
                    <div className="value">
                      <DashboardTooltip
                        id={uniqueId('bvp-billed')}
                        position="bottom"
                        value={_.sum(data.values.billed)}
                        label={currencyFormatterInShort(_.sum(data.values.billed))}
                        tooltipText={currencyFormatter(_.sum(data.values.billed))}
                      />
                    </div>
                  </div>
                  <PercentChangeIndicator percent={data.difference.billed} />
                </div>
                <div className="billed-paid-item">
                  <p
                    className="color-bar"
                    style={{
                      backgroundColor: DASHBOARD_COLORS.BILLED_VS_PAID[1],
                    }}
                  />
                  <div className="label-value">
                    <p className="label">Paid</p>
                    <div className="value">
                      <DashboardTooltip
                        id={uniqueId('bvp-paid')}
                        position="bottom"
                        value={_.sum(data.values.paid)}
                        label={currencyFormatterInShort(_.sum(data.values.paid))}
                        tooltipText={currencyFormatter(_.sum(data.values.paid))}
                      />
                    </div>
                  </div>
                  <PercentChangeIndicator percent={data.difference.paid} />
                </div>
                <div className="billed-paid-item">
                  <p
                    className="color-bar"
                    style={{
                      backgroundColor: DASHBOARD_COLORS.BILLED_VS_PAID[2],
                    }}
                  />
                  <div className="label-value">
                    <p className="label">Past Due</p>
                    <div className="value">
                      <DashboardTooltip
                        id={uniqueId('bvp-past-due')}
                        position="top"
                        value={_.sum(data.values.pastDue)}
                        label={currencyFormatterInShort(_.sum(data.values.pastDue))}
                        tooltipText={currencyFormatter(_.sum(data.values.pastDue))}
                      />
                    </div>
                  </div>
                  <PercentChangeIndicator percent={data.difference.pastDue} />
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

BilledVsPaidCard.propTypes = {
  title: PropTypes.string.isRequired,
  loader: PropTypes.bool.isRequired,
  tooltip: PropTypes.shape({
    cardTooltip: PropTypes.string.isRequired,
    totalCashflowTooltip: PropTypes.string.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    labels: PropTypes.shape([]).isRequired,
    values: {
      billed: PropTypes.shape([]).isRequired,
      paid: PropTypes.shape([]).isRequired,
    },
    difference: {
      billed: PropTypes.number.isRequired,
      paid: PropTypes.number.isRequired,
    },
    redirection: PropTypes.shape({}),
    lastUpdatedAt: PropTypes.string,
  }).isRequired,
};

export default BilledVsPaidCard;
